/**
 * Opening a rendered document — a report, a drift report, a service review —
 * in a new tab at its own address.
 *
 * It used to fetch the HTML and `document.write` it into a blank tab. That tab
 * inherited the console's CSP rather than the API's, so the API's per-document
 * `style-src 'sha256-…'` header never applied and the console had to allow
 * `style-src 'unsafe-inline'` for reports to render at all (ADR-0079). The tab
 * is now navigated to the report's `/api/…` URL, which the browser loads as an
 * ordinary same-origin document under the API's own headers (ADR-0080).
 *
 * The session is an HttpOnly, `SameSite=Lax`, `Path=/` cookie
 * (`packages/api/src/auth.ts`), so a same-origin top-level navigation carries
 * it exactly as `fetch` with `credentials: 'same-origin'` does. Nothing about
 * the session goes into the URL, and nothing needs to.
 */

export interface OpenReportOptions {
  /**
   * Open the browser's print dialogue over the report once it has rendered.
   * That dialogue is the whole of the PDF export: choosing "Save as PDF" as its
   * destination writes the file. There is no PDF library and no headless
   * browser anywhere in this product, so the PDF is the identical HTML through
   * the browser's own print pipeline and cannot disagree with the report about
   * a finding.
   */
  readonly print?: boolean;
}

/** Builds the error the console shows for a refused request. */
export type Refusal = (status: number, text: string) => Error;

export const POPUP_BLOCKED = 'The report opened in a new tab, which your browser blocked.';

const PRINT_POLL_MS = 100;
/** A minute. A report that has not loaded by then is not going to be printed by us. */
const PRINT_POLL_LIMIT = 600;

/**
 * Print the tab once the report has loaded in it.
 *
 * The report carries no script of its own — its CSP has no `script-src` — so
 * the console has to call `print()` from outside, and it cannot hang a `load`
 * listener on a document that does not exist yet. So it watches: the tab
 * starts on `about:blank`, whose readyState is already `complete`, and is ready
 * once it holds a different document that has finished loading. One frame
 * later the layout has been painted, which is what a first page needs to not
 * come out blank.
 *
 * Reading `target.document` needs the tab to stay same-origin and in the
 * console's browsing-context group. Both the console (nginx) and the API send
 * `Cross-Origin-Opener-Policy: same-origin`, and both are one origin, so the
 * handle survives the navigation. If the operator takes the tab somewhere else,
 * reading it throws and the watch simply ends — they have left the report, so
 * there is nothing to print.
 */
const printWhenLoaded = (target: Window): void => {
  let polls = 0;
  const timer = setInterval(() => {
    polls += 1;
    if (target.closed || polls > PRINT_POLL_LIMIT) {
      clearInterval(timer);
      return;
    }
    let loaded: boolean;
    try {
      loaded = target.document.URL !== 'about:blank' && target.document.readyState === 'complete';
    } catch {
      clearInterval(timer);
      return;
    }
    if (!loaded) return;
    clearInterval(timer);
    target.requestAnimationFrame(() => target.print());
  }, PRINT_POLL_MS);
};

/**
 * Probe the document, then send the tab to it.
 *
 * The probe is what keeps the console's error handling: a report that will not
 * open says why in the console, in a sentence, and the tab closes — rather than
 * the tab showing the API's JSON. On success the probe's body is cancelled
 * unread and the tab loads the document itself; the server renders it twice,
 * which for a stored report is a row read and for a service review a history
 * query, both small beside an operator waiting. HEAD would render it twice too
 * and could not say why it failed.
 *
 * The window is opened *before* the await deliberately: opening one after an
 * await is what the popup blocker stops.
 */
export const openDocument = async (
  path: string,
  options: OpenReportOptions,
  refuse: Refusal,
): Promise<void> => {
  const url = `/api${path}`;
  const target = window.open('', '_blank');
  try {
    // This does not go through `request`, so `credentials` is set here too —
    // without it the browser sends no cookie and the probe is a 401.
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw refuse(response.status, await response.text());
    // A failed cancel leaves a body nobody reads, which the browser discards;
    // it says nothing about whether the report will open.
    response.body?.cancel().catch(() => undefined);

    if (!target) throw refuse(0, POPUP_BLOCKED);
    // replace, not assign: Back from the report should not land on a blank page.
    target.location.replace(url);
    if (options.print) printWhenLoaded(target);
  } catch (cause) {
    target?.close();
    throw cause;
  }
};
